import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Snackbar,
  Tooltip,
  Typography
} from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import api from '../../api';
import { RecalibrationPromptDto } from '../../api/recalibration';
import SettingsCard from './SettingsCard';

/**
 * Calculator v3 — Calibration Prompts card.
 *
 * Shows tasks whose average observed productivity has drifted from the
 * tenant catalog rate, based on completed task iterations from the last
 * 90 days. Each prompt is resolved by one of three operator actions:
 *
 *   Apply          — adopts the observed rate as the new catalog rate
 *                    (every future bid uses it). This is the "our catalog
 *                    is stale, update it" path.
 *   Dismiss        — acknowledges the drift but keeps the catalog rate;
 *                    hidden for 30 days before reappearing on next scan.
 *   Special case   — flags the drift as a per-contract anomaly rather
 *                    than a catalog problem. Prompt is removed from queue.
 *
 * Silently hides for tenants without calculator_v3_enabled (the list
 * endpoint 403s, matching the MarginLadderSettingsCard pattern).
 */
const driftColor = (drift: number): string => {
  const abs = Math.abs(drift);
  if (abs >= 0.2) return '#B00020';
  if (abs >= 0.1) return '#C06A00';
  return '#1D7A3B';
};

const formatRate = (value: number, measureName?: string): string => {
  if (!Number.isFinite(value)) return '—';
  const rounded =
    value >= 10 ? Math.round(value).toLocaleString() : value.toFixed(2);
  if (!measureName) return rounded;
  return measureName.toLowerCase() === 'unit'
    ? `${rounded} hrs/unit`
    : `${rounded} ${measureName.toLowerCase()}/hr`;
};

const CalibrationPromptsCard: React.FC = function CalibrationPromptsCard() {
  const [prompts, setPrompts] = useState<RecalibrationPromptDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [scanning, setScanning] = useState<boolean>(false);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState<boolean>(false);
  const [snack, setSnack] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await api.RecalibrationApi.listPending();
      setPrompts(data);
      setHidden(false);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHidden(true);
      } else {
        setError(
          err?.response?.data?.message ??
            err?.message ??
            'Failed to load calibration prompts.'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runScan = async () => {
    setScanning(true);
    setError(null);
    try {
      const summary = await api.RecalibrationApi.scan();
      setSnack(
        `Scan complete — ${summary.promptsOpened} new, ${summary.promptsUpdated} refreshed.`
      );
      await load();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          'Scan failed. Try again in a moment.'
      );
    } finally {
      setScanning(false);
    }
  };

  const runApply = async (id: string) => {
    setActingOn(id);
    try {
      await api.RecalibrationApi.apply(id);
      setPrompts((prev) => prev.filter((p) => p.id !== id));
      setSnack('Rate updated. Future bids will use the new rate.');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          'Could not apply the new rate.'
      );
    } finally {
      setActingOn(null);
    }
  };

  const runDismiss = async (id: string) => {
    setActingOn(id);
    try {
      await api.RecalibrationApi.dismiss(id);
      setPrompts((prev) => prev.filter((p) => p.id !== id));
      setSnack('Dismissed. Hidden for 30 days.');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          'Could not dismiss.'
      );
    } finally {
      setActingOn(null);
    }
  };

  const runFlag = async (id: string) => {
    setActingOn(id);
    try {
      await api.RecalibrationApi.flagSpecialCase(id);
      setPrompts((prev) => prev.filter((p) => p.id !== id));
      setSnack('Flagged as a per-contract special case.');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          'Could not flag.'
      );
    } finally {
      setActingOn(null);
    }
  };

  const summary = useMemo(() => {
    if (error) return 'Unable to load calibration prompts.';
    if (prompts.length === 0) return 'No drift — catalog rates align with recent actuals.';
    const over = prompts.filter((p) => p.driftPercent > 0).length;
    const under = prompts.filter((p) => p.driftPercent < 0).length;
    return `${prompts.length} drifting task(s) · ${under} running slower than catalog · ${over} faster`;
  }, [error, prompts]);

  if (hidden) return null;

  return (
    <>
      <SettingsCard
        description="Tasks whose observed productivity from recent completed iterations differs materially from your catalog. Apply to update the catalog rate for all future bids, dismiss to acknowledge without changing, or flag as a per-contract special case."
        title="Catalog Calibration Prompts"
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between',
              mb: 2
            }}
          >
            <Typography color="secondary.400" variant="body_100">
              {loading ? 'Loading…' : summary}
            </Typography>
            <Button
              color="secondary"
              disabled={scanning || loading}
              onClick={runScan}
              size="small"
              variant="outlined"
            >
              {scanning ? 'Scanning…' : 'Scan now'}
            </Button>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Typography color="error.main" sx={{ mb: 2 }} variant="body_100">
              {error}
            </Typography>
          )}

          {!loading && prompts.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {prompts.map((p) => {
                const measureName = p.task?.measure?.name;
                const drift = Number(p.driftPercent ?? 0);
                const isSlower = drift < 0;
                const pct = Math.abs(drift * 100).toFixed(1);
                const acting = actingOn === p.id;
                return (
                  <Box
                    key={p.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'space-between',
                        mb: 1
                      }}
                    >
                      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                        {isSlower ? (
                          <TrendingDownIcon
                            fontSize="small"
                            sx={{ color: driftColor(drift) }}
                          />
                        ) : (
                          <TrendingUpIcon
                            fontSize="small"
                            sx={{ color: driftColor(drift) }}
                          />
                        )}
                        <Typography fontWeight="bold" variant="body_300">
                          {p.task?.name ?? 'Unknown task'}
                        </Typography>
                        <Chip
                          label={`${isSlower ? '−' : '+'}${pct}%`}
                          size="small"
                          sx={{
                            bgcolor: driftColor(drift),
                            color: 'white',
                            fontSize: '0.72rem',
                            fontWeight: 700
                          }}
                        />
                        <Tooltip
                          placement="top"
                          title={
                            isSlower
                              ? 'Technicians are taking LONGER than catalog predicts. Current bids likely under-price this work — margin pressure.'
                              : 'Technicians are FASTER than catalog predicts. Current bids may be over-priced — leaving deals on the table.'
                          }
                        >
                          <Chip
                            label={isSlower ? 'margin pressure' : 'over-priced'}
                            size="small"
                            sx={{ fontSize: '0.68rem' }}
                            variant="outlined"
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: '1fr 1fr 1fr',
                        mb: 1
                      }}
                    >
                      <Box>
                        <Typography
                          color="secondary.400"
                          variant="body_100"
                        >
                          Catalog rate
                        </Typography>
                        <Typography variant="body_200">
                          {formatRate(Number(p.catalogRate), measureName)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          color="secondary.400"
                          variant="body_100"
                        >
                          Observed rate
                        </Typography>
                        <Typography variant="body_200">
                          {formatRate(Number(p.observedRate), measureName)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          color="secondary.400"
                          variant="body_100"
                        >
                          Sample
                        </Typography>
                        <Typography variant="body_200">
                          {p.sampleSize} iterations · {p.windowDays}d
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end'
                      }}
                    >
                      <Button
                        color="secondary"
                        disabled={acting}
                        onClick={() => runFlag(p.id)}
                        size="small"
                        variant="text"
                      >
                        Flag special case
                      </Button>
                      <Button
                        color="secondary"
                        disabled={acting}
                        onClick={() => runDismiss(p.id)}
                        size="small"
                        variant="outlined"
                      >
                        Dismiss 30d
                      </Button>
                      <Button
                        color="primary"
                        disabled={acting}
                        onClick={() => runApply(p.id)}
                        size="small"
                        variant="contained"
                      >
                        {acting ? 'Applying…' : 'Apply new rate'}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {!loading && prompts.length === 0 && !error && (
            <Typography color="secondary.400" variant="body_100">
              Nothing to review. Catalog rates align with recent actuals within
              threshold.
            </Typography>
          )}
        </Box>
      </SettingsCard>
      <Snackbar
        autoHideDuration={3000}
        message={snack ?? ''}
        onClose={() => setSnack(null)}
        open={Boolean(snack)}
      />
    </>
  );
};

export default CalibrationPromptsCard;
